// Help text for the `context` subcommands.
import { FIELD_COPY } from '@driftless/topic-schema'
import { OVERBROAD_PATTERN_THRESHOLD } from './types'

export function printAddHelp(stream: NodeJS.WriteStream = process.stdout): void {
  stream.write(`Usage: driftless context add "<topic-slug>" [options]

Create a new context topic.

Required (one of):
  --pattern <glob>           Glob anchor (repeatable — pass multiple times for multi-anchor)
  --where <path>             Explicit file path anchor

Description:
  --what "<text>"            ${FIELD_COPY.what}
  --how "<text>"             ${FIELD_COPY.how}
  --decisions "<text>"       Why patterns were chosen (or --decision, repeatable)
  --gotchas "<text>"         Known traps (or --gotcha, repeatable)
  --invariant "<text>"       A must-be-true statement (repeatable)
  --check "<text>"           A required check/test that must pass (repeatable)
  --ownership "<text>"       Who owns this area

Anchoring:
  --pattern "src/auth/**"    Multi-glob anchor (REPLACE — use repeatedly)
  --where path/to/file.ts    Explicit file anchor
  --file <path>              Anchor a doc into the topic

Naming:
  <topic-slug>                  The stable id (kebab-case). Keep it specific.
  --title  "Short Name"         Human display name — short noun, like a diagram
                                  label ("MCP", "Billing"). NOT a sentence.
                                  Defaults to the slug if omitted.
  --what   "one sentence"       The summary shown in lists — the explanation goes
                                  here (and in --content), NOT in the title.

Status (trust model: Note → Knowledge):
  --status proposed             Submit for review now (it becomes a note up for review).
                                  Omit → born a Note (a draft only you see).
                                  Knowledge comes only from \`driftless context approve\` —
                                  an owner/admin act you run only when explicitly asked.
  --tags   tag1,tag2             Comma-separated topic tags
  --rel    <type>:<slug>         Typed relation (repeatable)
  --area   <name|id>             File the topic under an Area (a navigation
                                  domain). A new name auto-creates the area.

Content (markdown):
  --content "<text>"            ${FIELD_COPY.content}
  --content @path/to/file.md    Read content from file

Other:
  --dry-run                  Show what would happen, don't write
  --allow-secrets            Override the secret-scan (a field looks like an API
                               key / token / private key). The write is blocked
                               by default — pass this only to write it anyway.
  --json                     Machine-readable output
  --help                     This message

Examples:
  driftless context add "auth-boundaries" \\
    --what "Auth guards and token validation" \\
    --how "JWT verified by AuthGuard, refreshed by RefreshInterceptor" \\
    --pattern "src/auth/**"

  driftless context add "checkout-flow" \\
    --pattern "src/checkout/**" --pattern "src/cart/**" \\
    --what "Cart → checkout → payment"

  driftless context add "my-topic" \\
    --content "## What\nHandles authentication\n\n## How\nUses JWT tokens" \\
    --tags auth,security

Tip: a topic that ends up matching > ${OVERBROAD_PATTERN_THRESHOLD} components is almost always
over-broad. Healthy topics anchor 5–40. Split when in doubt.

Wiki-links: write [[other-slug]] inside --what/--how/--decisions/--gotchas to forward-
reference another topic. The API records it; the other topic shows you in its
'referenced by' list when read.
`)
}

export function printUpdateHelp(stream: NodeJS.WriteStream = process.stdout): void {
  stream.write(`Usage: driftless context update <topic-slug> [options]

Modify an existing context topic.

Scalar (REPLACE the field):
  --what "<text>"             ${FIELD_COPY.what}
  --how "<text>"              ${FIELD_COPY.how}
  --decisions "<text>"        Full replace — use --decision to APPEND instead
  --gotchas "<text>"          Full replace — use --gotcha to APPEND instead
  --invariants "<text>"       Full replace of invariants[] — use --invariant to APPEND (repeatable)
  --checks "<text>"           Full replace of required_checks[] — use --check to APPEND (repeatable)
  --ownership "<text>"
  --status <reviewed|draft|orphaned>
  --content "<text>" / --content @file.md
  --tags tag1,tag2
  --where <path>

Append (history-preserving — pass multiple times):
  --gotcha "<text>"           Append a gotcha (repeatable)
  --decision "<text>"         Append a decision (repeatable)
  --invariant "<text>"        Append an invariant (repeatable)
  --check "<text>"            Append a required check (repeatable)

Anchors (patterns[]):
  --pattern "<glob>"          REPLACE entire patterns[] (repeatable)
  --add-pattern "<glob>"      APPEND a pattern, idempotent (repeatable)
  --remove-pattern "<glob>"   REMOVE a pattern, idempotent (repeatable)

Relations:
  --rel <type>:<slug>         Add typed relation (repeatable)

Area:
  --area <name|id>            Migrate this topic into an Area (a navigation
                                domain). A new name auto-creates the area.

Enforcement:
  --enforce "<rule>"          Request a rule be created (or use --enforce alone)

Other:
  --dry-run                   Show what would happen, don't write
  --allow-secrets             Override the secret-scan (a field looks like an API
                                key / token / private key). Blocked by default.
  --json                      Machine-readable output
  --help                      This message

Examples:
  driftless context update auth-boundaries \\
    --gotcha "RefreshInterceptor mutates req.headers — clone before reading" \\
    --decision "Chose JWT over session for stateless horizontal scaling"

  driftless context update auth-boundaries \\
    --add-pattern "src/auth/refresh/**" --add-pattern "src/auth/oauth/**"

  driftless context update auth-boundaries --remove-pattern "src/auth/legacy/**"

Tip: batch related changes in ONE invocation — every PATCH bumps version
and writes an event. One PATCH with N flags ≫ N PATCHes with one flag each.

Wiki-links: inside any free-text value (--what/--how/--decision/--gotcha/--invariant/
--ownership) write [[other-slug]] and the API records a forward link automatically.
The other topic gets a backlink via 'referenced by' in 'context get'.
Slug grammar: lowercase alphanumerics + hyphens, starts with [a-z0-9]. Self-refs
are stripped; dead links are accepted silently and never produce a backlink.
  driftless context update refund-flow \\
    --gotcha "Same race as [[stripe-webhook-ingest]]"
`)
}

export function printRelationsHelp(stream: NodeJS.WriteStream = process.stdout): void {
  stream.write(`Usage: driftless context relations <topic-slug>

List a topic's links — both kinds, in separate sections:
  - typed relations (--rel) — strong, carry a type (depends_on, blocks, …)
  - [[slug]] mentions — weak links written inline in any free-text field

Examples:
  driftless context relations auth-boundaries
`)
}

export function printGraphHelp(stream: NodeJS.WriteStream = process.stdout): void {
  stream.write(`Usage: driftless context graph <center-slug> [--depth N]

Render the local topic graph around a center topic.

Options:
  --depth N    Exploration depth (default: 3)

Examples:
  driftless context graph auth-boundaries
  driftless context graph auth-boundaries --depth 2
`)
}
